import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, FileText, Download, Receipt, FilePlus, Calendar, DollarSign } from 'lucide-react';
import { clientesApi, relacionesApi, facturasApi } from '../api/endpoints';
import { descargarRelacionPdf } from '../api/pdf';
import { Spinner } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { useAuthStore } from '../auth/authStore';

const estatusColor = {
  Borrador: 'bg-amber-500/15 text-amber-400',
  Generada: 'bg-cyan-500/15 text-cyan-400',
  Facturada: 'bg-emerald-500/15 text-emerald-400',
};

export function Relaciones() {
  const usuario = useAuthStore((s) => s.usuario);
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [relaciones, setRelaciones] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [descargando, setDescargando] = useState(new Set());

  useEffect(() => {
    (async () => {
      const cs = await clientesApi.list();
      setClientes(cs);
      const first = cs[0];
      if (first) {
        setCliente(first.id);
      }
    })();
  }, []);

  useEffect(() => {
    if (!cliente) return;
    (async () => {
      setRelaciones(null);
      const [r, u] = await Promise.all([relacionesApi.list(cliente), relacionesApi.ultima(cliente)]);
      setRelaciones(r);
      setLastSync(u);

      if (u) {
        const next = new Date(u.fechaFin);
        next.setDate(next.getDate() + 1);
        const today = new Date();
        setSuggestion({ fechaInicio: next.toISOString().slice(0, 10), fechaFin: today.toISOString().slice(0, 10), lastPeriod: u.periodoLabel });
      } else {
        const today = new Date();
        const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
        setSuggestion({ fechaInicio: monthAgo.toISOString().slice(0, 10), fechaFin: today.toISOString().slice(0, 10), lastPeriod: null });
      }
    })();
  }, [cliente]);

  const handleGenerarFactura = async (rel) => {
    if (!confirm(`¿Generar factura para relación #${rel.id}?`)) return;
    try {
      await facturasApi.create({ clienteId: cliente, relacionId: rel.id });
      const updated = await relacionesApi.list(cliente);
      setRelaciones(updated);
      navigate('/facturas');
    } catch (e) {
      alert('Error: ' + (e?.response?.data?.error || e.message));
    }
  };

  const handleDescargarPdf = async (relId) => {
    setDescargando((s) => new Set(s).add(relId));
    try {
      await descargarRelacionPdf(relId);
    } catch (e) {
      const data = e?.response?.data;
      const msg =
        data?.title || data?.error
        || (data?.errors && Object.values(data.errors).flat().join(' · '))
        || e?.message
        || 'Error generando PDF';
      alert(`No se pudo generar el PDF: ${msg}`);
    } finally {
      setDescargando((s) => { const n = new Set(s); n.delete(relId); return n; });
    }
  };

  return (
    <div className="p-3 sm:p-6 space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <select value={cliente || ''} onChange={(e) => setCliente(Number(e.target.value))} className="px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white min-w-[200px]">
          {clientes.map(c => <option key={c.id} value={c.id}>{c.nombreComercial}</option>)}
        </select>
        <div className="flex-1" />
        {suggestion && cliente && (
          <div className="text-xs text-muted bg-panel-2 px-3 py-2 rounded border border-border flex items-center gap-2">
            <Calendar size={12} />
            {suggestion.lastPeriod
              ? <>Última: <strong className="text-cyan-400">{suggestion.lastPeriod}</strong> · Sugerida desde <strong>{suggestion.fechaInicio}</strong></>
              : <>Sin relaciones previas. Sugerida desde <strong>{suggestion.fechaInicio}</strong></>
            }
          </div>
        )}
        <Link to="/relaciones/nueva" state={{ clienteId: cliente, suggestion }} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-sm font-semibold rounded-lg shadow-lg shadow-cyan-500/20 shrink-0">
          <FilePlus size={16} /> <span className="hidden sm:inline">Nueva Relación</span>
        </Link>
      </div>

      {relaciones === null ? (
        <div className="flex justify-center py-12"><Spinner size={32} /></div>
      ) : relaciones.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="Sin relaciones de soporte"
          description={suggestion?.lastPeriod
            ? `Última fue ${suggestion.lastPeriod}. Crea la siguiente desde ${suggestion.fechaInicio}.`
            : 'Aún no se han generado relaciones de soporte.'}
          action={<Link to="/relaciones/nueva" state={{ clienteId: cliente, suggestion }} className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-md text-sm font-semibold"><Plus size={14} /> Crear primera</Link>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {relaciones.map((r) => (
            <div key={r.id} className="bg-panel border border-border rounded-xl p-4 hover:border-cyan-500/40 transition group">
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0 flex-1">
                  <span className="text-[10px] font-mono text-muted">#{r.id}</span>
                  <h3 className="font-semibold text-white">{r.periodoLabel}</h3>
                  <p className="text-xs text-slate-400">{new Date(r.fechaInicio).toLocaleDateString('es-MX')} → {new Date(r.fechaFin).toLocaleDateString('es-MX')}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-2">
                  <button
                    onClick={() => handleDescargarPdf(r.id)}
                    disabled={descargando.has(r.id)}
                    className="p-1.5 rounded text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
                    title="Descargar PDF (Ctrl/Cmd+click para previsualizar)"
                  >
                    {descargando.has(r.id) ? <Spinner size={14} /> : <Download size={14} />}
                  </button>
                  {r.estatus !== 'Facturada' && usuario?.rol === 'Admin' && (
                    <button onClick={() => handleGenerarFactura(r)} className="p-1.5 rounded text-slate-400 hover:text-amber-400 hover:bg-amber-500/10" title="Generar Factura">
                      <Receipt size={14} />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted mb-3">{r.itemsCount} items</p>
              <div className="space-y-1 pt-2 border-t border-border">
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Subtotal</span>
                  <span className="text-slate-300 font-mono">${Number(r.subtotal).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted">IVA (16%)</span>
                  <span className="text-slate-300 font-mono">${Number(r.iva).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-sm pt-1 border-t border-border">
                  <span className="text-muted font-semibold">Total</span>
                  <span className="text-emerald-400 font-mono font-bold">${Number(r.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-border mt-2">
                <span></span>
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${estatusColor[r.estatus] || 'bg-slate-500/15 text-slate-400'}`}>{r.estatus.toUpperCase()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
